<template>
  <section class="guided-analyses-should-you-bluff">
    <readonly-alert v-if="!authorized" />
    <saved-scenario-alert
      v-if="savedScenario"
      v-bind:title="savedScenario.title"
    />
    <header class="page-header">
      <h1>Guided Analysis: Should you bluff?</h1>
    </header>
    <h4>Step 1: What street are you considering bluffing?</h4>
    <street-selector
      v-bind:street="state.streetToBluff"
      v-bind:authorized="authorized"
      v-on:setStreet="setStreetToBluff($event)"
    />
    <h4>Step 2: How many opponents are you facing on that street?</h4>
    <num-opponents-selector
      v-bind:numOpponents="state.players.numPlayers"
      v-bind:authorized="authorized"
      v-on:addOpponent="addPlayer()"
      v-on:removeOpponent="removePlayer()"
    />
    <h4>Step 3: What are your hole cards?</h4>
    <hole-cards-selector
      v-bind:presetCommunityCards="state.presetCommunityCards"
      v-bind:presetHoleCards="state.presetHoleCards"
      v-bind:popoverDirectionClass="'dropdown'"
      v-bind:disabled="!authorized"
      v-on:setPresetHoleCard="setPresetHoleCard($event)"
      v-on:removePresetHoleCard="removePresetHoleCard($event)"
    />
    <preflop-steps
      ref="preflopSteps"
      v-bind:players="state.players"
      v-bind:presetCommunityCards="state.presetCommunityCards"
      v-bind:streetToBluff="state.streetToBluff"
      v-bind:authorized="authorized"
    />
    <flop-steps
      v-bind:state="state"
      v-bind:textWhenFiveCardHandPercentagesArentUpToDate="getTextWhenFiveCardHandPercentagesArentUpToDate(1)"
      v-bind:authorized="authorized"
      v-on:setIndividualPresetCommunityCard="setIndividualPresetCommunityCard($event.card, $event.position)"
      v-on:removePresetCommunityCard="removePresetCommunityCard($event)"
      v-on:setBoard="setBoard($event)"
      v-on:setBluffSize="setBluffSize($event)"
      v-on:setProfitableBreakevenPercentage="setProfitableBreakevenPercentage($event)"
    />
    <turn-steps
      v-if="(state.streetToBluff === 'turn' || state.streetToBluff === 'river') && state.presetCommunityCards.flopSet"
      v-bind:state="state"
      v-bind:textWhenFiveCardHandPercentagesArentUpToDate="getTextWhenFiveCardHandPercentagesArentUpToDate(2)"
      v-bind:authorized="authorized"
      v-on:setIndividualPresetCommunityCard="setIndividualPresetCommunityCard($event.card, $event.position)"
      v-on:removePresetCommunityCard="removePresetCommunityCard($event)"
      v-on:setBluffSize="setBluffSize($event)"
      v-on:setProfitableBreakevenPercentage="setProfitableBreakevenPercentage($event)"
    />
    <river-steps
      v-if="state.streetToBluff === 'river' && state.presetCommunityCards.turnSet"
      v-bind:state="state"
      v-bind:textWhenFiveCardHandPercentagesArentUpToDate="getTextWhenFiveCardHandPercentagesArentUpToDate(3)"
      v-bind:authorized="authorized"
      v-on:setIndividualPresetCommunityCard="setIndividualPresetCommunityCard($event.card, $event.position)"
      v-on:removePresetCommunityCard="removePresetCommunityCard($event)"
      v-on:setBluffSize="setBluffSize($event)"
      v-on:setProfitableBreakevenPercentage="setProfitableBreakevenPercentage($event)"
    />
    <section v-if="conclusionsVisible">
      <hr>
      <initial-conclusion
        v-bind:bluffSize="state.bluffSize"
        v-bind:streetToBluff="state.streetToBluff"
        v-bind:hitPercentageForStreetToBluff="hitPercentageForStreetToBluff"
        v-bind:foldEquity="foldEquity"
        v-bind:immediatelyProfitableBreakevenPercentage="$store.getters['guidedAnalysesBluffing/immediatelyProfitableBreakevenPercentage']"
        v-bind:cumulativeHitPercentages="$store.getters['guidedAnalysesBluffing/cumulativeHitPercentages']"
        v-bind:numPlayers="state.players.numPlayers"
      />
      <final-conclusion
        v-if="state.streetToBluff !== 'river'"
        v-bind:ableToRunEquityCalculations="ableToRunEquityCalculations"
        v-bind:equitySimulationsInProgress="equitySimulationsInProgress"
        v-bind:equity="equity"
        v-bind:bluffSize="state.bluffSize"
        v-bind:streetToBluff="state.streetToBluff"
        v-bind:hitPercentageForStreetToBluff="hitPercentageForStreetToBluff"
        v-bind:foldEquity="foldEquity"
        v-bind:immediatelyProfitableBreakevenPercentage="$store.getters['guidedAnalysesBluffing/immediatelyProfitableBreakevenPercentage']"
        v-bind:profitableBreakevenPercentage="state.profitableBreakevenPercentage"
        v-on:setProfitableBreakevenPercentage="setProfitableBreakevenPercentage($event)"
      />
      <long-run-disclaimer/>
      <hr>
      <create-sharable-url-button
        v-bind:disabled="!authorized"
        v-on:createSharableUrl="$emit('createSharableUrl', {
          method: 'createSharableGuidedAnalysesBluffingURL',
          state: state,
          approximateWaitTime: 5,
        })"
      />
      <save-scenario-button
        v-bind:savedScenario="savedScenario"
        v-bind:disabled="!authorized"
        v-on:saveScenario="$emit('saveScenario', {
          method: 'createSharableGuidedAnalysesBluffingURL',
          savedScenarioPropertyName: 'shouldYouBluff',
          state: state,
        })"
        v-on:updateScenario="$emit('updateScenario', {
          method: '_constructGuidedAnalysesBluffingData',
          state: state,
        })"
      />
    </section>
    <onboarding-modal
      toolName="should you bluff tool"
      urlPath="should-you-bluff"
    />
    <help-icon path="/help/should-you-bluff" />
  </section>
</template>

<script>
import ReadonlyAlert from '@/components/utilities/readonly-alert';
import SavedScenarioAlert from '@/components/utilities/saved-scenario-alert';
import HoleCardsSelector from '@/components/controls/card-selection/hole-cards-selector';
import StreetSelector from '@/components/guided-analyses/street-selector';
import NumOpponentsSelector from '@/components/guided-analyses/num-opponents-selector';
import PreflopSteps from '@/components/guided-analyses/should-you-bluff/preflop-steps';
import FlopSteps from '@/components/guided-analyses/should-you-bluff/flop-steps';
import TurnSteps from '@/components/guided-analyses/should-you-bluff/turn-steps';
import RiverSteps from '@/components/guided-analyses/should-you-bluff/river-steps';
import InitialConclusion from '@/components/guided-analyses/should-you-bluff/conclusions/initial-conclusion';
import FinalConclusion from '@/components/guided-analyses/should-you-bluff/conclusions/final-conclusion';
import LongRunDisclaimer from '@/components/guided-analyses/should-you-bluff/conclusions/long-run-disclaimer';
import CreateSharableUrlButton from '@/components/controls/create-sharable-url-button';
import SaveScenarioButton from '@/components/controls/save-scenario-button';
import OnboardingModal from '@/components/onboarding-modal';
import HelpIcon from '@/components/help-icon';
import simulator from '@/services/simulator/simulator';
import _ from 'lodash';

export default {
  name: 'guided-analyses-should-you-bluff',
  components: {
    'readonly-alert': ReadonlyAlert,
    'saved-scenario-alert': SavedScenarioAlert,
    'hole-cards-selector': HoleCardsSelector,
    'street-selector': StreetSelector,
    'num-opponents-selector': NumOpponentsSelector,
    'preflop-steps': PreflopSteps,
    'flop-steps': FlopSteps,
    'turn-steps': TurnSteps,
    'river-steps': RiverSteps,
    'initial-conclusion': InitialConclusion,
    'final-conclusion': FinalConclusion,
    'long-run-disclaimer': LongRunDisclaimer,
    'create-sharable-url-button': CreateSharableUrlButton,
    'save-scenario-button': SaveScenarioButton,
    'onboarding-modal': OnboardingModal,
    'help-icon': HelpIcon,
  },
  data: function () {
    return {
      equitySimulationsInProgress: false,
    };
  },
  computed: {
    state: function () {
      return this.$store.state.guidedAnalysesBluffing;
    },
    savedScenario: function () {
      let routeId = this.$route.params.id;

      if (!this.authorized) {
        return false;
      }

      return this.$store.state.account.user.savedScenarios.shouldYouBluff.find(function (savedScenario) {
        return savedScenario.url.includes(routeId);
      });
    },
    hitPercentageForStreetToBluff: function () {
      if (this.state.streetToBluff === 'flop') {
        return this.state.players.find(0).states[1].hitPercentage;
      } else if (this.state.streetToBluff === 'turn') {
        return this.state.players.find(0).states[2].hitPercentage;
      } else if (this.state.streetToBluff === 'river') {
        return this.state.players.find(0).states[3].hitPercentage;
      }
    },
    foldEquity: function () {
      if (this.state.players.numPlayers === 1) {
        return (100 - this.hitPercentageForStreetToBluff).roundToTwoDecimalPlaces();
      }

      else {
        return this.$store.getters['guidedAnalysesBluffing/percentageOfTimeEveryoneMissesTheBoard'](this.state.streetToBluff).roundToTwoDecimalPlaces();
      }
    },
    equity: function () {
      let equity = 100;

      this.state.players.forEach(function (player) {
        equity -= player.states[0].equity;
      });

      return equity.roundToTwoDecimalPlaces();
    },
    ableToRunFiveCardHandPercentageSimulationsForTheFlop: function () {
      let returnValue = true;

      if (!this.state.presetCommunityCards.flopSet) {
        returnValue = false;
      }


      if (this.state.players.hasEmptyRange('preflop')) {
        returnValue = false;
      }

      return returnValue;
    },
    ableToRunFiveCardHandPercentageSimulationsForTheTurn: function () {
      let returnValue = true;

      if (!this.state.presetCommunityCards.turnSet) {
        returnValue = false;
      }

      if (this.state.players.hasEmptyRange('flop')) {
        returnValue = false;
      }

      return returnValue;
    },
    ableToRunFiveCardHandPercentageSimulationsForTheRiver: function () {
      let returnValue = true;

      if (!this.state.presetCommunityCards.riverSet) {
        returnValue = false;
      }

      if (this.state.players.hasEmptyRange('turn')) {
        returnValue = false;
      }

      return returnValue;
    },
    ableToRunEquityCalculations: function () {
      let ableToRunEquityCalculations = true;

      if (!this.state.presetHoleCards.bothSet) {
        ableToRunEquityCalculations = false;
      }

      else if (this.state.streetToBluff === 'flop') {
        if (!this.state.presetCommunityCards.flopSet) {
          ableToRunEquityCalculations = false;
        }

        if (this.state.players.hasEmptyRange('flop')) {
          ableToRunEquityCalculations = false;
        }
      }

      else if (this.state.streetToBluff === 'turn') {
        if (!this.state.presetCommunityCards.turnSet) {
          ableToRunEquityCalculations = false;
        }

        if (this.state.players.hasEmptyRange('turn')) {
          ableToRunEquityCalculations = false;
        }
      }

      else if (this.state.streetToBluff === 'river') {
        if (!this.state.presetCommunityCards.riverSet) {
          ableToRunEquityCalculations = false;
        }

        if (this.state.players.hasEmptyRange('river')) {
          ableToRunEquityCalculations = false;
        }
      }

      return ableToRunEquityCalculations;
    },
    playerStateIndexToUseForSimulations: function () {
      if (this.state.streetToBluff === 'flop') {
        return 1;
      } else if (this.state.streetToBluff === 'turn') {
        return 2;
      } else if (this.state.streetToBluff === 'river') {
        return 3;
      }
    },
    presetCards: function () {
      return _.union(this.state.presetHoleCards.array, this.state.presetCommunityCards.array);
    },
    conclusionsVisible: function () {
      if (this.state.streetToBluff === 'flop') {
        return this.state.presetCommunityCards.flopSet;
      } else if (this.state.streetToBluff === 'turn') {
        return this.state.presetCommunityCards.turnSet;
      } else if (this.state.streetToBluff === 'river') {
        return this.state.presetCommunityCards.riverSet;
      }
    },
    authorized: function () {
      return this.$store.getters['account/authorizedForBasic'];
    },
  },
  mounted: function () {
    let self = this;

    if (self.$route.params.id) {
      self.load();
    }

    else {
      self.$store.commit('guidedAnalysesBluffing/setFiveCardHandValuesToDefaults', {
        settings: self.$store.state.account.user.settings,
        opponentTightness: 'straightforward',
      });
    }

    self.handleOnboard();

    // flop five card hand simulations
    self.$watch(function () {
      let sum = 0;

      self.state.players.forEach(function (player) {
        sum += player.states[0].generalHands.numberOfSpecificHandsInRange;
      });

      return sum;
    }, _.debounce(self.runFiveCardHandPercentagesSimulationsForFlop, 1000));

    // turn five card hand simulations
    self.$watch(function () {
      let sum = 0;

      self.state.players.forEach(function (player) {
        sum += player.states[1].generalHands.numberOfSpecificHandsInRange;
      });

      return sum;
    }, _.debounce(self.runFiveCardHandPercentagesSimulationsForTurn, 1000));

    // river five card hand simulations
    self.$watch(function () {
      let sum = 0;

      self.state.players.forEach(function (player) {
        sum += player.states[2].generalHands.numberOfSpecificHandsInRange;
      });

      return sum;
    }, _.debounce(self.runFiveCardHandPercentagesSimulationsForRiver, 1000));

    // equity
    self.$watch(
      function () {
        let sum = 0;

        self.state.players.forEach(function (player) {
          sum += player.states[0].generalHands.numberOfSpecificHandsInRange;
          sum += player.states[1].generalHands.numberOfSpecificHandsInRange;
          sum += player.states[2].generalHands.numberOfSpecificHandsInRange;
          sum += player.states[3].generalHands.numberOfSpecificHandsInRange;
        });

        return sum;
      },
      _.debounce(self.runEquityCalculations, 1000)
    );
  },
  methods: {
    // mounted
    load: function () {
      let self = this;

      self.$http.get(`${process.env.BASE_API_URL}/sharable-url-store/` + self.$route.params.id)
        .then(function (response) {
          if (!response || !response.data.storeState) {
            alert('Didn\'t get data back from server. Redirecting to the default guided analysis bluffing page.');
            self.$router.push('/guided-analyses/should-you-bluff');
          } else {
            self.$store.commit('guidedAnalysesBluffing/setState', response.data.storeState);
            self.simulate();
          }
        })
        .catch(function () {
          alert('URL doesn\'t exist. Redirecting to the default guided analysis bluffing page.');
          self.$router.push('/guided-analyses/should-you-bluff');
        })
      ;
    },
    handleOnboard: function () {
      let self = this;

      if (
        !self.authorized ||
        !self.$store.state.account.user.settings.onboarding ||
        self.$store.state.account.user.settings.onboarding.shouldYouBluff
      ) {
        return;
      }

      $(self.$el).find('.onboarding-modal').modal();
      self.$http
        .patch(`${process.env.BASE_API_URL}/user/onboard`, { page: 'shouldYouBluff' })
        .then(function () {
          self.$store.commit('account/onboard', 'shouldYouBluff');
        })
        .catch(function () {
          console.log('Error: Unable to onboard.');
        })
      ;
    },

    // stuff
    setStreetToBluff: function (streetToBluff) {
      this.$store.commit('guidedAnalysesBluffing/setStreetToBluff', streetToBluff);

      if (streetToBluff === 'flop') {
        this.$store.commit('guidedAnalysesBluffing/removePresetCommunityCard', 'turn');
        this.$store.commit('guidedAnalysesBluffing/removePresetCommunityCard', 'river');
        this.setRangesAccordingToFiltersForAllPlayers();
      } else if (streetToBluff === 'turn') {
        this.$store.commit('guidedAnalysesBluffing/removePresetCommunityCard', 'river');
        this.setRangesAccordingToFiltersForAllPlayers();
      }
    },
    setBluffSize: function (bluffSize) {
      this.$store.commit('guidedAnalysesBluffing/setBluffSize', bluffSize);
    },
    setProfitableBreakevenPercentage: function (profitableBreakevenPercentage) {
      this.$store.commit('guidedAnalysesBluffing/setProfitableBreakevenPercentage', profitableBreakevenPercentage);
    },
    getTextWhenFiveCardHandPercentagesArentUpToDate: function (playerStateIndex) {
      if (
        (playerStateIndex === 1 && this.ableToRunFiveCardHandPercentageSimulationsForTheFlop) ||
        (playerStateIndex === 2 && this.ableToRunFiveCardHandPercentageSimulationsForTheTurn) ||
        (playerStateIndex === 3 && this.ableToRunFiveCardHandPercentageSimulationsForTheRiver)
      ) {
        return 'Running simulations...';
      } else {
        return 'Percentages of made hands, draws and combos are not up to date. Make sure that the range in the previous step has at least one hand selected so that simulations can be run to update the percentages.';
      }
    },

    // preset cards
    setPresetHoleCard: function (params) {
      this.$store.commit('guidedAnalysesBluffing/removePresetHoleCard', params.position);
      this.$store.commit('guidedAnalysesBluffing/setPresetHoleCard', {
        card: params.card,
        position: params.position
      });
      this.resetAllDoubleSliders();
      this.setRangesAccordingToFiltersForAllPlayers();
      this.simulate();
    },
    removePresetHoleCard: function (position) {
      this.$store.commit('guidedAnalysesBluffing/removePresetHoleCard', position);
      this.setRangesAccordingToFiltersForAllPlayers();
      this.simulate();
    },
    setIndividualPresetCommunityCard: function (card, position) {
      this.$store.commit('guidedAnalysesBluffing/removePresetCommunityCard', position);
      this.$store.commit('guidedAnalysesBluffing/setIndividualPresetCommunityCard', {
        card: card,
        position: position
      });
      this.resetAllDoubleSliders();
      this.setRangesAccordingToFiltersForAllPlayers();
      this.simulate();
    },
    setBoard: function (boardCards) {
      this.$store.commit('guidedAnalysesBluffing/removePresetCommunityCard', 'flop1');
      this.$store.commit('guidedAnalysesBluffing/removePresetCommunityCard', 'flop2');
      this.$store.commit('guidedAnalysesBluffing/removePresetCommunityCard', 'flop3');
      this.$store.commit('guidedAnalysesBluffing/removePresetCommunityCard', 'turn');
      this.$store.commit('guidedAnalysesBluffing/removePresetCommunityCard', 'river');
      this.$store.commit('guidedAnalysesBluffing/setBoard', boardCards);
      this.resetAllDoubleSliders();
      this.setRangesAccordingToFiltersForAllPlayers();
      this.simulate();
    },
    removePresetCommunityCard: function (position) {
      this.$store.commit('guidedAnalysesBluffing/removePresetCommunityCard', position);
      this.setRangesAccordingToFiltersForAllPlayers();
      this.simulate();
    },
    addPlayer: function () {
      this.$store.commit('guidedAnalysesBluffing/addPlayer', {
        settings: this.$store.state.account.user.settings,
        opponentTightness: 'straightforward',
      });
    },
    removePlayer: function () {
      this.$store.commit('guidedAnalysesBluffing/removePlayer', this.state.players.numPlayers - 1);
      this.simulate();
    },

    setRangesAccordingToFiltersForAllPlayers: function () {
      this.state.players.forEach(function (player, i) {
        this.$store.commit('guidedAnalysesBluffing/setRangesAccordingToFilters', i);
      }, this);
    },
    resetAllDoubleSliders: function () {
      this.$refs.preflopSteps.resetAllDoubleSliders();
    },

    // simulate
    simulate: function () {
      this
        .runEquityCalculations()
        .then(this.runFiveCardHandPercentagesSimulationsForFlop)
        .then(this.runFiveCardHandPercentagesSimulationsForTurn)
        .then(this.runFiveCardHandPercentagesSimulationsForRiver)
      ;
    },
    runEquityCalculations: function () {
      if (!this.ableToRunEquityCalculations) {
        return Promise.resolve();
      }

      let self = this;
      let weightedRanges = self._getWeightedRanges();
      let numberOfSimulations = self.$store.state.account.user.settings.equityCalculator.numberOfSimulations;

      return simulator
        .getEquities(weightedRanges, self.state.presetCommunityCards.array, numberOfSimulations, function () {
          self.equitySimulationsInProgress = true;
        })
        .then(function (simulationResults) {
          self.$store.commit('guidedAnalysesBluffing/updateEquityResults', {
            simulationResults: simulationResults,
            numberOfSimulations: numberOfSimulations,
            rakePercentage: self.$store.state.account.user.settings.equityCalculator.rakeAndTip.rakePercentage,
            tipPercentage: self.$store.state.account.user.settings.equityCalculator.rakeAndTip.tipPercentage
          });
          self.equitySimulationsInProgress = false;
        })
        .catch(function (e) {
          console.error(e);
          alert('Encountered an unexpected problem while running the simulations.');
        })
      ;
    },
    _getWeightedRanges: function () {
      let weightedRanges = [];
      let heroWeightedRange = [
        [this.state.presetHoleCards.holeCardOne, this.state.presetHoleCards.holeCardTwo]
      ];
      let playerStateIndexToUseForSimulations = this.playerStateIndexToUseForSimulations;

      weightedRanges.push(heroWeightedRange);
      this.state.players.forEach(function (player) {
        weightedRanges.push(player.states[playerStateIndexToUseForSimulations].generalHands.weightedRange);
      }, this);

      return weightedRanges;
    },
    runFiveCardHandPercentagesSimulationsForFlop: function () {
      let self = this;

      if (!self.ableToRunFiveCardHandPercentageSimulationsForTheFlop) {
        return Promise.resolve();
      }

      return simulator
        .getFlopFiveCardHandPercentages(self.state.players, self.state.presetCommunityCards)
        .then(function () {
          self.setRangesAccordingToFiltersForAllPlayers();
          self.$store.commit('guidedAnalysesBluffing/setLastSimulationInfo', 1);
        })
        .catch(function (e) {
          console.error(e);
          alert('Encountered an unexpected problem while running the simulations.');
        })
      ;    },
    runFiveCardHandPercentagesSimulationsForTurn: function () {
      let self = this;

      if (!self.ableToRunFiveCardHandPercentageSimulationsForTheTurn) {
        return Promise.resolve();
      }

      return simulator
        .getTurnFiveCardHandPercentages(self.state.players, self.state.presetCommunityCards)
        .then(function () {
          self.setRangesAccordingToFiltersForAllPlayers();
          self.$store.commit('guidedAnalysesBluffing/setLastSimulationInfo', 2);
        })
        .catch(function (e) {
          console.error(e);
          alert('Encountered an unexpected problem while running the simulations.');
        })
      ;
    },
    runFiveCardHandPercentagesSimulationsForRiver: function () {
      let self = this;

      if (!self.ableToRunFiveCardHandPercentageSimulationsForTheRiver) {
        return Promise.resolve();
      }

      return simulator
        .getRiverFiveCardHandPercentages(self.state.players, self.state.presetCommunityCards)
        .then(function () {
          self.setRangesAccordingToFiltersForAllPlayers();
          self.$store.commit('guidedAnalysesBluffing/setLastSimulationInfo', 3);
        })
        .catch(function (e) {
          console.error(e);
          alert('Encountered an unexpected problem while running the simulations.');
        })
      ;
    },
  },
};
</script>

<style lang="scss" scoped>
  iframe {
    max-width: 100%;
  }

  .num-opponents {
    width: 100px;
  }

  .create-sharable-url-button-container, .save-scenario-button-container {
    width: 100%;
  }

  .save-scenario-button-container {
    margin-top: 10px;
  }
</style>



// WEBPACK FOOTER //
// src/components/guided-analyses/should-you-bluff/should-you-bluff.vue